import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader, XCircle } from 'lucide-react';
import API_URL from '../config/API_URL';

const OneTimePaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('');
  const [creditsAdded, setCreditsAdded] = useState(0);
  const [featureName, setFeatureName] = useState('');
  const [redirectPath, setRedirectPath] = useState('/dashboard');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const featureParam = searchParams.get('feature');
    
    if (!sessionId) {
      setStatus('error');
      setMessage('Invalid payment session');
      return;
    }

    processPayment(sessionId, featureParam);
  }, [searchParams]);

  const getRedirectPath = (featureName) => {
    const routeMap = {
      'transcribe': '/dashboard/transcribe',
      'linkedin_search': '/dashboard/calendar',
      'topic_briefing': '/dashboard/topic-scheduler',  // ✨ Topic Intelligence redirect
      'news_scheduler': '/dashboard/news-scheduler',
    };
    
    return routeMap[featureName] || '/dashboard';
  };

  const processPayment = async (sessionId, featureParam) => {
    try {
      const response = await fetch(
        `${API_URL}/api/subscription/process-onetime-payment/${sessionId}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');
        setMessage(data.message);
        setCreditsAdded(data.credits_remaining || 0);
        setFeatureName(data.feature_name || featureParam || '');
        
        // ✨ ENHANCED: Smart redirect logic with topic_briefing support
        const basePath = getRedirectPath(data.feature_name || featureParam);
        const redirectUrl = `${basePath}?from_payment=${data.feature_name || featureParam}`;
        setRedirectPath(redirectUrl);
        
        console.log('✅ Payment processed, redirecting to:', redirectUrl);
        
        // Redirect after 3 seconds
        setTimeout(() => {
          navigate(redirectUrl);
        }, 3000);
      } else {
        setStatus('error');
        setMessage(data.message || 'Payment processing failed');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      setStatus('error');
      setMessage('Failed to process payment. Please contact support.');
    }
  };

  const getFeatureDisplayName = (featureName) => {
    const displayNames = {
      'transcribe': 'AI Call Transcriber',
      'linkedin_search': 'LinkedIn Connection Search',
      'topic_briefing': 'Topic Intelligence',  // ✨ UPDATED
      'news_scheduler': 'News Scheduler',
    };
    
    return displayNames[featureName] || 'the feature';
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '2rem',
    },
    card: {
      background: 'white',
      borderRadius: '24px',
      padding: '3rem',
      maxWidth: '500px',
      width: '100%',
      textAlign: 'center',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    },
    iconContainer: {
      marginBottom: '2rem',
      display: 'flex',
      justifyContent: 'center',
    },
    title: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#0f172a',
      marginBottom: '1rem',
      fontFamily: "'Syne', sans-serif",
    },
    message: {
      fontSize: '1.1rem',
      color: '#64748b',
      marginBottom: '2rem',
      lineHeight: 1.6,
    },
    creditsDisplay: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '1.5rem',
      borderRadius: '12px',
      marginBottom: '2rem',
    },
    creditsNumber: {
      fontSize: '3rem',
      fontWeight: 800,
      marginBottom: '0.5rem',
    },
    creditsLabel: {
      fontSize: '1rem',
      opacity: 0.9,
    },
    featureTag: {
      display: 'inline-block',
      background: '#eff6ff',
      color: '#1e40af',
      padding: '0.5rem 1rem',
      borderRadius: '8px',
      fontSize: '0.9rem',
      fontWeight: 600,
      marginBottom: '1.5rem',
    },
    redirectMessage: {
      fontSize: '0.9rem',
      color: '#64748b',
      marginTop: '2rem',
    },
    button: {
      padding: '1rem 2rem',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
    },
    spinner: {
      width: '60px',
      height: '60px',
      border: '6px solid #e2e8f0',
      borderTop: '6px solid #667eea',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
  };

  return (
    <>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      
      <div style={styles.container}>
        <div style={styles.card}>
          {status === 'processing' && (
            <>
              <div style={styles.iconContainer}>
                <div style={styles.spinner} />
              </div>
              <h1 style={styles.title}>Processing Payment...</h1>
              <p style={styles.message}>
                Please wait while we confirm your purchase.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div style={styles.iconContainer}>
                <CheckCircle size={80} color="#10b981" />
              </div>
              <h1 style={styles.title}>Payment Successful! 🎉</h1>
              
              {featureName && (
                <div style={styles.featureTag}>
                  {getFeatureDisplayName(featureName)}
                </div>
              )}
              
              <p style={styles.message}>{message}</p>
              
              {creditsAdded > 0 && (
                <div style={styles.creditsDisplay}>
                  <div style={styles.creditsNumber}>{creditsAdded}</div>
                  <div style={styles.creditsLabel}>
                    {creditsAdded === 1 ? 'Credit' : 'Credits'} Available
                  </div>
                </div>
              )}
              
              <p style={styles.redirectMessage}>
                Redirecting you back to continue...
              </p>
              
              <button
                onClick={() => navigate(redirectPath)}
                style={styles.button}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Go Now
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div style={styles.iconContainer}>
                <XCircle size={80} color="#ef4444" />
              </div>
              <h1 style={styles.title}>Payment Failed</h1>
              <p style={styles.message}>{message}</p>
              
              <button
                onClick={() => navigate('/dashboard')}
                style={styles.button}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Return to Dashboard
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default OneTimePaymentSuccess;